import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  MonetizationOn as LoanIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountIcon,
} from '@mui/icons-material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const LoanAnalytics = ({ data, meta }) => {
  if (!data) return null;

  // Loan Status Distribution
  const statusData = {
    labels: data.byStatus?.map(item => item._id.charAt(0).toUpperCase() + item._id.slice(1)) || [],
    datasets: [
      {
        data: data.byStatus?.map(item => item.count) || [],
        backgroundColor: [
          '#4caf50', // approved
          '#2196f3', // active
          '#ff9800', // pending
          '#f44336', // rejected
          '#9c27b0', // completed
          '#607d8b', // others
        ],
        hoverBackgroundColor: [
          '#66bb6a',
          '#64b5f6',
          '#ffb74d',
          '#e57373',
          '#ba68c8',
          '#78909c',
        ],
      }
    ]
  };

  // Loan Type Distribution
  const typeData = {
    labels: data.byType?.map(item => item.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())) || [],
    datasets: [
      {
        label: 'Loan Amount (₹)',
        data: data.byType?.map(item => item.totalAmount) || [],
        backgroundColor: [
          '#1976d2',
          '#2e7d32',
          '#f57c00',
          '#9c27b0',
          '#d32f2f',
          '#7b1fa2',
          '#0288d1',
          '#388e3c',
          '#f57f17',
          '#6a1b9a'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Monthly Loan Application Trend
  const monthlyTrendData = {
    labels: data.monthlyTrend?.map(item => {
      const [month, year] = item.month.split('/');
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }) || [],
    datasets: [
      {
        label: 'Loan Applications',
        data: data.monthlyTrend?.map(item => item.count) || [],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#1976d2',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        yAxisID: 'y',
      },
      {
        label: 'Total Amount (₹)',
        data: data.monthlyTrend?.map(item => item.amount) || [],
        borderColor: '#f57c00',
        backgroundColor: 'rgba(245, 124, 0, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#f57c00',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
      },
    },
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Applications'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Total Amount (₹)'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Loan Analytics Overview
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LoanIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {data.total || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Loans</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountIcon sx={{ color: '#2e7d32', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  {data.active || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Active Loans</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#f57c00', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                  ₹{data.keyMetrics?.totalLoanAmount?.toLocaleString() || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Loan Amount</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccountIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                  {data.completed || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Completed Loans</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Loan Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LoanIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Loan Status Distribution
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <Doughnut data={statusData} options={pieOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Loan Types by Amount */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AccountIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Loan Types by Amount
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Bar
                  data={typeData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Loan Trend */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Monthly Loan Application Trend
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Line
                  data={monthlyTrendData}
                  options={lineOptions}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Insights */}
      {data.repaymentSummary && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Repayment Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: '#e8f5e8'
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    ₹{data.repaymentSummary.totalPaid?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Amount Paid
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: '#fff3e0'
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#f57c00' }}>
                    ₹{data.repaymentSummary.totalOutstanding?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Outstanding Amount
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  textAlign: 'center',
                  bgcolor: '#e3f2fd'
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {data.keyMetrics?.repaymentRate?.toFixed(1) || 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Repayment Rate
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {data.amountDistribution && data.amountDistribution.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Loan Amount Distribution
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {data.amountDistribution.map((range, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {range.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {range.range} Range
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 1 }}>
                      ₹{range.totalAmount?.toLocaleString() || 0} Total
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LoanAnalytics;
